import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  YouTube,
  Email,
  Phone,
  LocationOn,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';

const FooterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  marginTop: 'auto',
}));

const FooterSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  textDecoration: 'none',
  display: 'block',
  marginBottom: theme.spacing(1),
  '&:hover': {
    color: '#a4adb5',
    textDecoration: 'underline',
  },
}));

const SocialIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  '&:hover': {
    color: '#a4adb5',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const Footer = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentYear = new Date().getFullYear();
  
  // Check if we're on a dashboard route (admin or seller)
  const isDashboardRoute = location.pathname.startsWith('/admin') || 
                          location.pathname.startsWith('/seller') ||
                          location.pathname.startsWith('/order-management');
  
  // Get sidebar state from localStorage (set by dashboards)
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  
  React.useEffect(() => {
    // Listen for sidebar state changes via localStorage
    const checkSidebarState = () => {
      const collapsed = localStorage.getItem('sidebarCollapsed') === 'true';
      setSidebarCollapsed(collapsed);
    };
    
    // Check initial state
    checkSidebarState();
    
    // Listen for storage events (when sidebar state changes in another tab/window)
    window.addEventListener('storage', checkSidebarState);
    
    // Also listen for custom event (for same-tab updates)
    window.addEventListener('sidebarStateChanged', checkSidebarState);
    
    // Poll for changes (fallback for same-tab updates)
    const interval = setInterval(checkSidebarState, 100);
    
    return () => {
      window.removeEventListener('storage', checkSidebarState);
      window.removeEventListener('sidebarStateChanged', checkSidebarState);
      clearInterval(interval);
    };
  }, []);
  
  // Sidebar width - adjust based on typical dashboard sidebar widths
  // Admin: 280px (expanded) / 80px (collapsed)
  // Seller: 280px (expanded) / 70px (collapsed)
  // On mobile, sidebar is usually hidden or overlay, so no margin needed
  const isAdminRoute = location.pathname.startsWith('/admin');
  const collapsedWidth = isAdminRoute ? 80 : 70; // Admin uses 80px, Seller uses 70px when collapsed
  const sidebarWidth = isMobile ? 0 : (sidebarCollapsed ? collapsedWidth : 280);
  
  const footerSections = {
    company: {
      title: 'Company',
      links: [
        { label: 'About Us', href: '/about' },
        { label: 'Our Story', href: '/our-story' },
        { label: 'Careers', href: '/careers' },
        { label: 'Press', href: '/press' },
        { label: 'Blog', href: '/blog' },
        // { label: 'Custom Solutions', href: '/CustomSol' },
      ],
    },
    products: {
      title: 'Products',
      links: [
        { label: 'Marble', href: '/products?category=marble' },
        { label: 'Granite', href: '/products?category=granite' },
        { label: 'Tiles', href: '/products?category=tiles' },
        { label: 'Natural Stone', href: '/products?category=natural-stone' },
        { label: 'Featured Products', href: '/products?featured=true' },
      ],
    },
    services: {
      title: 'Services',
      links: [
        // { label: 'Become a Seller', href: '/register?type=seller' },
        // { label: 'Bulk Orders', href: '/bulk-orders' },
        // { label: 'Custom Solutions', href: '/CustomSol' },
        // { label: 'Installation Services', href: '/installation' },
        // { label: 'Quality Assurance', href: '/Quality' },
      ],
    },
    support: {
      title: 'Support',
      links: [
        // { label: 'Help Center', href: '/help' },
        { label: 'Contact Us', href: '/contact' },
        { label: 'Shipping Info', href: '/shipping' },
        { label: 'Returns & Refunds', href: '/ReturnRefund' },
        { label: 'Track Your Order', href: '/track-order' },
        { label: 'Become a Seller', href: '/register?type=seller' },
        { label: 'Quality Assurance', href: '/Quality' },

      ],
    },
    legal: {
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', href: '/privacy' },
        { label: 'Terms of Service', href: '/terms' },
        { label: 'Cookie Policy', href: '/cookies' },
        { label: 'Seller Agreement', href: '/seller-terms' },
        { label: 'Compliance', href: '/compliance' },
      ],
    },
  };
  
  const socialLinks = [
    { icon: <Facebook />, href: 'https://facebook.com/mosmarketplace', label: 'Facebook' },
    { icon: <Twitter />, href: 'https://twitter.com/mosmarketplace', label: 'Twitter' },
    { icon: <Instagram />, href: 'https://www.instagram.com/luxstonesjaipur?igsh=MWpxdDdiOG1mNXF6Mg==', label: 'Instagram' },
    { icon: <LinkedIn />, href: 'https://linkedin.com/company/mosmarketplace', label: 'LinkedIn' },
    { icon: <YouTube />, href: 'https://youtube.com/mosmarketplace', label: 'YouTube' },
  ];
  
  const contactInfo = [
    {
      icon: <Email />,
      text: 'connect@mallofstones.com',
      href: 'mailto:connect@mallofstones.com',
    },
    {
      icon: <Phone />,
      text: '+91 9460061819',
      href: 'tel:+919460061819',
    },
    {
      icon: <LocationOn />,
      text: 'Jaipur, Rajasthan',
      href: 'https://maps.google.com',
    },
  ];
  
  return (
    <FooterContainer
      sx={{
        // Adjust margin-left for dashboard routes to account for sidebar
        marginLeft: isDashboardRoute && !isMobile ? `${sidebarWidth}px` : 0,
        transition: theme.transitions.create(['margin-left', 'width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        // Ensure footer doesn't get cut off on smaller screens
        width: isDashboardRoute && !isMobile ? `calc(100% - ${sidebarWidth}px)` : '100%',
        // Ensure footer is positioned correctly and doesn't overflow
        position: 'relative',
        zIndex: 1,
        // On mobile, ensure full width
        ...(isMobile && {
          marginLeft: 0,
          width: '100%',
        }),
      }}
    >
      <Container maxWidth="lg">
        <Box py={6}>
          <Grid container spacing={4}>
            {/* Company Info */}
            <Grid item xs={12} md={3}>
              <FooterSection>
                <Typography
                  variant="h5"
                  component="div"
                  sx={{
                    fontWeight: 720,
                    mb: 2,
                    background: `linear-gradient( #f7e7ce)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: '#f7e7ce',
                  }}
                >
                  Mall of Stones
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
                  India's premier marketplace for premium building materials. 
                  Connecting quality suppliers with discerning customers since 2024.
                </Typography>
                
                {/* Contact Info */}
                <Box sx={{ mb: 3 }}>
                  {contactInfo.map((contact, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ mr: 1, opacity: 0.8 }}>{contact.icon}</Box>
                      <FooterLink
                        href={contact.href}
                        sx={{ mb: 0, fontSize: '0.875rem' }}
                      >
                        {contact.text}
                      </FooterLink>
                    </Box>
                  ))}
                </Box>
                
                {/* Social Links */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Follow Us
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {socialLinks.map((social, index) => (
                      <SocialIconButton
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.label}
                        size="small"
                      >
                        {social.icon}
                      </SocialIconButton>
                    ))}
                  </Box>
                </Box>
              </FooterSection>
            </Grid>
            
            {/* Footer Links */}
            {Object.entries(footerSections).map(([key, section]) => (
              <Grid item xs={6} sm={4} md={2} key={key}>
                <FooterSection>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}
                  >
                    {section.title}
                  </Typography>
                  {section.links.map((link, index) => (
                    <FooterLink
                      key={index}
                      href={link.href}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {link.label}
                    </FooterLink>
                  ))}
                </FooterSection>
              </Grid>
            ))}
            
            {/* Newsletter Signup */}
            <Grid item xs={12} md={2}>
              <FooterSection>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}
                >
                  Newsletter
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                  Get the latest updates on new products and exclusive offers.
                </Typography>
                <Box
                  component="form"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <input
                    type="email"
                    placeholder="Enter your email"
                    style={{
                      padding: '8px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      outline: 'none',
                    }}
                  />
                  <button
                    type="submit"
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#A0826D',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    Subscribe
                  </button>
                </Box>
              </FooterSection>
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
        
        {/* Bottom Section */}
        <Box
          py={3}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            © {currentYear} MOS Marketplace. All rights reserved.
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              justifyContent: 'center',
            }}
          >
            <FooterLink href="/privacy" sx={{ mb: 0, fontSize: '0.875rem' }}>
              Privacy
            </FooterLink>
            <FooterLink href="/terms" sx={{ mb: 0, fontSize: '0.875rem' }}>
              Terms
            </FooterLink>
            <FooterLink href="/cookies" sx={{ mb: 0, fontSize: '0.875rem' }}>
              Cookies
            </FooterLink>
            <FooterLink href="/sitemap" sx={{ mb: 0, fontSize: '0.875rem' }}>
              Sitemap
            </FooterLink>
            {/* <FooterLink href="/CustomSol" sx={{ mb: 0, fontSize: '0.875rem' }}>
              Custom Solutions
            </FooterLink> */}
          </Box>
          
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Made with ❤️ in India
          </Typography>
        </Box>
      </Container>
    </FooterContainer>
  );
};

export default Footer;